<template>
    <q-table
        v-model:pagination="localPagination"
        table-class="ubs-table"
        :columns="columns"
        :rows="data"
        :row-key="uniqueProp"
        @request="onRequest"
    >
        <template v-if="customBody" #body="props">
            <slot name="customBody" :props="props"></slot>
        </template>
    </q-table>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { Column } from "@/models/Table";
import { Pagination } from "@/models/Pagination";

const defaultPagination = {
    sortBy: "desc",
    descending: false,
    page: 1,
    rowsPerPage: 50,
    rowsNumber: 0,
};

export default defineComponent({
    name: "Table",
    props: {
        data: {
            type: Object as PropType<unknown[]>,
            required: true,
        },
        columns: {
            type: Object as PropType<Column<unknown>[]>,
            required: true,
        },
        uniqueProp: {
            type: String,
            default: "id",
        },
        customBody: {
            type: Boolean,
            default: false,
        },
        pagination: {
            type: Object as PropType<Pagination>,
            default: () => defaultPagination,
        },
    },
    emits: ["update:pagination"],
    data() {
        return {
            // local pagination is a copy of the prop pagination,
            // it is used as a value for v-model
            localPagination: defaultPagination,
        };
    },
    watch: {
        pagination: {
            handler: function (oldValue, newValue) {
                if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
                    this.localPagination = {
                        ...defaultPagination,
                        ...this.pagination,
                    };
                }
            },
            deep: true,
            immediate: true,
        },
    },
    methods: {
        onRequest({ pagination }: { pagination: Pagination } & unknown) {
            this.$emit("update:pagination", pagination);
        },
    },
});
</script>

<style lang="scss">
@import "src/styles/variables";
@import "src/styles/typography/typography-mixins";
.ubs-table {
    .q-table {
        th {
            @include caption;
        }

        tr:nth-child(even) {
            background-color: $color-section-main;
        }

        tbody td {
            @include body-2;
            vertical-align: top;
        }
    }
}
</style>
